import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LessonCard from './components/LessonCard';
import { allStatisticsLessons, unitNames } from './data';
import SEOHead from '../../../components/SEOHead';

const APStatisticsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<string>('all');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: 'AP Statistics Full Curriculum | All 9 Units | Augustine Anyimadu-Ahenkae',
      description: 'Complete AP Statistics curriculum covering all 9 College Board units: exploring data, sampling, probability, and statistical inference. Includes 270+ practice questions and comprehensive test preparation.',
      url: `${siteUrl}/grades/ap-statistics`,
      provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
      educationalLevel: 'Advanced Placement',
      inLanguage: 'en',
      hasCourseInstance: [
        { '@type': 'CourseInstance', name: 'Unit 1: Exploring One-Variable Data (15-23%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 2: Exploring Two-Variable Data (5-7%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 3: Collecting Data (12-15%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 4: Probability & Distributions (10-20%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 5: Sampling Distributions (7-12%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 6: Inference for Proportions (12-15%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 7: Inference for Means (10-18%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 8: Chi-Square Tests (2-5%)', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 9: Inference for Slopes (2-5%)', courseMode: 'online' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What units are on the AP Statistics exam?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The AP Statistics exam covers 9 units: Exploring One-Variable Data, Exploring Two-Variable Data, Collecting Data, Probability & Distributions, Sampling Distributions, Inference for Proportions, Inference for Means, Chi-Square Tests, and Inference for Slopes.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the format of the AP Statistics exam?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The AP Statistics exam has two sections: Section I is 40 multiple-choice questions in 90 minutes (50% of score), and Section II has 6 free-response questions in 90 minutes (50% of score), including 1 investigative task.',
          },
        },
      ],
    },
  ];

  const filteredLessons = allStatisticsLessons.filter(lesson => {
    const matchesSearch = lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.concept.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUnit = selectedUnit === 'all' || lesson.unit === selectedUnit;
    return matchesSearch && matchesUnit;
  });

  const lessonsByUnit = unitNames.map(unitName => ({
    unit: unitName,
    lessons: filteredLessons.filter(l => l.unit === unitName)
  })).filter(group => group.lessons.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <SEOHead
        title="AP Statistics Full Curriculum | All 9 Units | Augustine Anyimadu-Ahenkae"
        description="Master AP Statistics with comprehensive lessons covering all 9 College Board units. Exploratory data analysis, probability, sampling distributions, and statistical inference. 270+ practice questions and AP exam prep."
        canonical="/grades/ap-statistics"
        keywords="AP Statistics curriculum, exploratory data analysis, statistical inference, confidence intervals, hypothesis testing, chi-square tests, AP Statistics exam prep"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-6">
          <Link 
            to="/grades/hub" 
            className="inline-flex items-center text-white hover:text-blue-100 mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Grades Hub
          </Link>
          <h1 className="text-5xl font-bold mb-4">AP Statistics</h1>
          <p className="text-xl text-blue-100 mb-6 max-w-3xl">
            Master statistical thinking with comprehensive coverage of all 9 AP Statistics units. From exploratory data analysis to inference, build the skills needed for AP exam success and beyond.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center bg-white/20 px-4 py-2 rounded-lg">
              <i className="ri-book-line mr-2 text-2xl"></i>
              <div>
                <div className="text-2xl font-bold">{allStatisticsLessons.length}</div>
                <div className="text-sm">Comprehensive Lessons</div>
              </div>
            </div>
            <div className="flex items-center bg-white/20 px-4 py-2 rounded-lg">
              <i className="ri-question-line mr-2 text-2xl"></i>
              <div>
                <div className="text-2xl font-bold">
                  {allStatisticsLessons.reduce((sum, lesson) => sum + lesson.practiceQuestions.length, 0)}
                </div>
                <div className="text-sm">Practice Questions</div>
              </div>
            </div>
            <div className="flex items-center bg-white/20 px-4 py-2 rounded-lg">
              <i className="ri-trophy-line mr-2 text-2xl"></i>
              <div>
                <div className="text-2xl font-bold">9</div>
                <div className="text-sm">AP Exam Units</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        {/* Search and Filter */}
        <div className="mb-8 bg-white rounded-lg shadow-md p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search lessons by topic or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <i className="ri-filter-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <select
                value={selectedUnit}
                onChange={(e) => setSelectedUnit(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Units</option>
                {unitNames.map((unit, idx) => (
                  <option key={idx} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* AP Exam Info */}
        <div className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow-md p-6 border-l-4 border-purple-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center">
            <i className="ri-file-list-3-line mr-2 text-purple-600"></i>
            AP Statistics Exam Overview
          </h2>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Section I: Multiple Choice</h3>
              <ul className="space-y-1 text-gray-700">
                <li>• 40 questions in 90 minutes</li>
                <li>• 50% of exam score</li>
                <li>• Covers all 9 units</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Section II: Free Response</h3>
              <ul className="space-y-1 text-gray-700">
                <li>• 6 questions in 90 minutes</li>
                <li>• 50% of exam score</li>
                <li>• Includes 1 investigative task</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Key Skills</h3>
              <ul className="space-y-1 text-gray-700">
                <li>• Selecting methods</li>
                <li>• Data analysis</li>
                <li>• Statistical argumentation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Unit Breakdown */}
        <div className="mb-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Units &amp; Exam Weighting</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: 'Unit 1: Exploring One-Variable Data', weight: '15-23%' },
              { name: 'Unit 2: Exploring Two-Variable Data', weight: '5-7%' },
              { name: 'Unit 3: Collecting Data', weight: '12-15%' },
              { name: 'Unit 4: Probability & Distributions', weight: '10-20%' },
              { name: 'Unit 5: Sampling Distributions', weight: '7-12%' },
              { name: 'Unit 6: Inference for Proportions', weight: '12-15%' },
              { name: 'Unit 7: Inference for Means', weight: '10-18%' },
              { name: 'Unit 8: Chi-Square Tests', weight: '2-5%' },
              { name: 'Unit 9: Inference for Slopes', weight: '2-5%' }
            ].map((unit, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <div className="font-semibold text-gray-900 text-sm mb-1">{unit.name}</div>
                <div className="text-2xl font-bold text-blue-600">{unit.weight}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Lessons */}
        {lessonsByUnit.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <i className="ri-search-line text-6xl text-gray-400 mb-4"></i>
            <p className="text-xl text-gray-600">No lessons found matching your search.</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedUnit('all');
              }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          lessonsByUnit.map((group, groupIdx) => (
            <div key={groupIdx} className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg flex items-center justify-center mr-3">
                  {groupIdx + 1}
                </span>
                {group.unit}
              </h2>
              {group.lessons.map((lesson, lessonIdx) => (
                <LessonCard key={lessonIdx} lesson={lesson} />
              ))}
            </div>
          ))
        )}
      </div>

      {/* Additional Resources */}
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Additional Resources</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Formula Sheet</h3>
              <p className="text-gray-600 mb-4">
                Download the official AP Statistics formula sheet provided during the exam.
              </p>
              <a
                href="https://apstudents.collegeboard.org/ap/pdf/ap-statistics-formula-sheet.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold"
              >
                View Formula Sheet
                <i className="ri-external-link-line ml-1"></i>
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-table-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Statistical Tables</h3>
              <p className="text-gray-600 mb-4">
                Access z-tables, t-tables, chi-square tables, and more for reference.
              </p>
              <a
                href="https://apstudents.collegeboard.org/ap/pdf/ap-statistics-tables.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold"
              >
                View Tables
                <i className="ri-external-link-line ml-1"></i>
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AP Central</h3>
              <p className="text-gray-600 mb-4">
                Visit College Board&apos;s AP Statistics page for past exams and scoring guidelines.
              </p>
              <a
                href="https://apcentral.collegeboard.org/courses/ap-statistics"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-purple-600 hover:text-purple-700 font-semibold"
              >
                Visit AP Central
                <i className="ri-external-link-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APStatisticsPage;